import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { eventKeysApi } from "@/api/event-keys";
import { useSession } from "@/stores/session";
import { useEnvStore } from "@/stores/env";
import { canInEnv } from "@/lib/authz";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { EnvironmentsCard } from "@/components/environments-card";
import { formatRelative } from "@/lib/format";
import { KeyRound } from "lucide-react";

export const Route = createFileRoute("/_authed/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const qc = useQueryClient();
  const user = useSession((s) => s.user);
  // Event keys are environment-scoped, so gate on the effective role in the
  // active environment (falls back to the account role until it loads).
  const envRole = useEnvStore((s) => s.activeRole());
  const canKeys = canInEnv(envRole, user?.role, "manage_keys");

  const keysQ = useQuery({ queryKey: ["event-keys"], queryFn: () => eventKeysApi.list() });

  const [newName, setNewName] = useState("");
  // Off by default, matching the server: a key called an "event key" should
  // not arrive able to read every run unless somebody ticked the box.
  const [newReadsRuns, setNewReadsRuns] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [revoke, setRevoke] = useState<{ id: string; name: string } | null>(null);

  const create = useMutation({
    mutationFn: () => eventKeysApi.create(newName, newReadsRuns ? ["runs:read"] : []),
    onSuccess: (res) => {
      setCreatedKey(res.key);
      setNewName("");
      setNewReadsRuns(false);
      void qc.invalidateQueries({ queryKey: ["event-keys"] });
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => eventKeysApi.del(id),
    onSuccess: () => { setRevoke(null); void qc.invalidateQueries({ queryKey: ["event-keys"] }); },
  });

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Account" title="Settings" />

      <EnvironmentsCard />

      <Card className="overflow-hidden">
        <div className="border-b px-5 py-3">
          <h2 className="font-display text-[15px] font-semibold tracking-tight">Event keys</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Use these with the <code className="font-mono">Authorization: Bearer &lt;key&gt;</code> header when posting to <code className="font-mono">/api/v1/events</code>.
          </p>
        </div>

        {canKeys && (
          <div className="p-5">
            <form
              onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
              className="flex items-end gap-2"
            >
              <div className="flex-1 space-y-1.5"><Label htmlFor="key-name">Name</Label>
                <Input id="key-name" required value={newName} onChange={(e) => setNewName(e.target.value)} /></div>
              <div className="space-y-1.5 pb-2">
                <Label htmlFor="key-runs-read" className="flex items-center gap-2 text-[13px] font-normal">
                  <input
                    id="key-runs-read"
                    type="checkbox"
                    className="size-3.5 accent-brand"
                    checked={newReadsRuns}
                    onChange={(e) => setNewReadsRuns(e.target.checked)}
                  />
                  Can read runs
                </Label>
              </div>
              <Button type="submit" disabled={create.isPending || !newName} className="gap-1.5 bg-brand text-primary-foreground hover:bg-brand/90">
                <KeyRound size={14} /> {create.isPending ? "Creating…" : "Create key"}
              </Button>
            </form>

            {createdKey && (
              <div className="mt-4 rounded-lg border border-wait/30 bg-wait/10 p-3">
                <div className="text-sm font-medium text-wait">New key — copy it now, it won't be shown again</div>
                <code className="mt-1 block break-all font-mono text-xs text-foreground">{createdKey}</code>
                <Button size="sm" variant="ghost" className="mt-2 h-7 text-wait hover:text-wait" onClick={() => setCreatedKey(null)}>Dismiss</Button>
              </div>
            )}
          </div>
        )}

        {(keysQ.data?.data ?? []).length === 0 ? (
          <div className="border-t px-5 py-8 text-center text-sm text-muted-foreground">No keys yet</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5 font-mono text-[11px] uppercase tracking-wider">Name</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">Can</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">Last used</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">Created</TableHead>
                <TableHead className="pr-5"><span className="sr-only">Actions</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(keysQ.data?.data ?? []).map((k) => (
                <TableRow key={k.id}>
                  <TableCell className="pl-5 text-[13px] font-medium">{k.name}</TableCell>
                  {/* What the key can do, on the row. A credential whose powers
                      are invisible is how an "event key" ended up reading every
                      run in the environment with nobody aware of it. */}
                  <TableCell className="font-mono text-[12px] text-muted-foreground">
                    {(k.scopes ?? ["events:write"]).join(", ")}
                  </TableCell>
                  <TableCell className="tabular font-mono text-[13px] text-muted-foreground">{k.last_used_at ? formatRelative(k.last_used_at) : "never"}</TableCell>
                  <TableCell className="tabular font-mono text-[13px] text-muted-foreground">{formatRelative(k.created_at)}</TableCell>
                  <TableCell className="pr-5 text-right">
                    {canKeys && (
                      <Button variant="ghost" size="sm" className="h-7 text-fail hover:text-fail"
                        onClick={() => setRevoke({ id: k.id, name: k.name })}>
                        Revoke
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {revoke && (
        <ConfirmDialog
          open={!!revoke} onOpenChange={(o) => !o && setRevoke(null)}
          title={`Revoke event key "${revoke.name}"?`}
          description="Any application currently using this key will start receiving 401 errors on POST /api/v1/events. This cannot be undone."
          confirmLabel="Revoke key"
          loading={del.isPending}
          onConfirm={() => del.mutate(revoke.id)}
        />
      )}
    </div>
  );
}
